import {
  Alert,
  AlertIcon,
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  usePrefersReducedMotion,
} from '@chakra-ui/react';
import { Button } from '@diamond/shared/ui';
import { ChangeEvent, useRef, useState } from 'react';

import { ExclusionRule } from '../api';
import {
  downloadImportTemplate,
  ImportPreview,
  readImportFile,
} from '../utils/import';

export function ImportDrawer({
  isOpen,
  onClose,
  onSubmit,
  pending,
  canWrite,
  serverError,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (items: ExclusionRule[]) => Promise<void>;
  pending: boolean;
  canWrite: boolean;
  serverError: string;
}) {
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [error, setError] = useState('');
  const [reading, setReading] = useState(false);
  const [filename, setFilename] = useState('');
  const [downloading, setDownloading] = useState(false);
  const generation = useRef(0);
  const reducedMotion = usePrefersReducedMotion();
  const readFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const current = ++generation.current;
    setPreview(null);
    setError('');
    setFilename(file?.name ?? '');
    if (!file) return;
    setReading(true);
    try {
      const result = await readImportFile(file);
      if (current === generation.current) setPreview(result);
    } catch (error) {
      if (current === generation.current)
        setError(
          error instanceof Error
            ? error.message
            : 'The workbook could not be read.'
        );
    } finally {
      if (current === generation.current) setReading(false);
    }
  };
  const download = async () => {
    setDownloading(true);
    setError('');
    try {
      await downloadImportTemplate();
    } catch {
      setError('Could not download the template. Try again.');
    } finally {
      setDownloading(false);
    }
  };
  const invalid = preview?.rows.some((row) => row.errors.length > 0) ?? true;
  return (
    <Drawer
      isOpen={isOpen}
      onClose={pending ? () => undefined : onClose}
      placement="right"
      size="xl"
      closeOnOverlayClick={!pending}
      closeOnEsc={!pending}
    >
      <DrawerOverlay />
      <DrawerContent
        motionProps={
          reducedMotion ? { transition: { duration: 0 } } : undefined
        }
      >
        <DrawerCloseButton isDisabled={pending} />
        <DrawerHeader>Import exclusions</DrawerHeader>
        <DrawerBody>
          <Stack spacing={5}>
            <Text color="gray.600">
              Preview up to 1,000 rules, then add them in one transaction.
              Existing rules remain unchanged.
            </Text>
            <Button
              variant="outline"
              onClick={download}
              isLoading={downloading}
            >
              Download template
            </Button>
            <FormControl>
              <FormLabel htmlFor="exclusion-file">Excel file</FormLabel>
              <Input
                id="exclusion-file"
                type="file"
                accept=".xlsx"
                p={2}
                h="auto"
                onChange={readFile}
                isDisabled={pending || reading}
              />
              <Text mt={2} fontSize="sm" color="gray.600">
                .xlsx · max 5 MiB · CG1 codes such as 01 should use text cells.
              </Text>
            </FormControl>
            {reading && <Text role="status">Reading workbook…</Text>}
            {(error || serverError) && (
              <Alert status="error" role="alert">
                <AlertIcon />
                {error || serverError}
              </Alert>
            )}
            {preview && (
              <>
                <Box aria-live="polite">
                  <Text fontWeight="semibold">{filename}</Text>
                  <Text fontSize="sm" color="gray.600">
                    {preview.rows.length} rows · {preview.items.length} unique
                    valid rules · {preview.duplicateCount} duplicates
                  </Text>
                </Box>
                {invalid && (
                  <Alert status="error">
                    <AlertIcon />
                    Fix the highlighted rows before importing. No request has
                    been sent.
                  </Alert>
                )}
                <Box overflowX="auto">
                  <Table size="sm">
                    <Thead>
                      <Tr>
                        {[
                          'Row',
                          'Product',
                          'Channel',
                          'Plant',
                          'CG1',
                          'Validation',
                        ].map((label) => (
                          <Th key={label}>{label}</Th>
                        ))}
                      </Tr>
                    </Thead>
                    <Tbody>
                      {preview.rows.map((row) => (
                        <Tr
                          key={row.row}
                          bg={row.errors.length ? 'red.50' : undefined}
                        >
                          <Td>{row.row}</Td>
                          {row.values.map((value, index) => (
                            <Td key={index}>{value || 'Any'}</Td>
                          ))}
                          <Td>
                            <Text
                              color={row.errors.length ? 'red.700' : 'gray.600'}
                              fontSize="sm"
                            >
                              {row.errors.join(' ') || 'Valid'}
                            </Text>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </>
            )}
          </Stack>
        </DrawerBody>
        <DrawerFooter gap={3}>
          <Button variant="outline" onClick={onClose} isDisabled={pending}>
            Cancel
          </Button>
          <Button
            variant="solid"
            onClick={() => preview && onSubmit(preview.items)}
            isDisabled={
              !canWrite || invalid || reading || !preview?.items.length
            }
            isLoading={pending}
          >
            Import rules
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
